/*
Pure helpers for the Loyalty > Prizes ledger: status vocabulary, cell
formatting, sorting and filtering.

CommonJS on purpose, like cmsTabs.js — the colocated test runs under plain
`node` because this package has no test framework.

The statuses mirror derivePrizeStatus in
ps-back/graphql/resolvers/queries/admin/aux/loyaltyPrizePipelines.js. Keep the
two lists in step.
*/
const PRIZE_STATUS = {
  // Fans won it and there is no code pool at all — they cannot claim. Loudest.
  'no-codes': { label: 'No codes uploaded', tone: 'bad' },
  'sold-out': { label: 'Sold out', tone: 'bad' },
  'low-stock': { label: 'Low stock', tone: 'warn' },
  unclaimed: { label: 'Never claimed', tone: 'warn' },
  stalled: { label: 'Stalled', tone: 'quiet' },
  active: { label: 'Active', tone: 'good' },
  // Minted per fan by AccessTicket at claim time (the My Primavera Card), so it
  // has no pool to run down. Deliberately not a 'bad' tone: every stock verdict
  // is meaningless here, not alarming.
  'on-demand': { label: 'On demand', tone: 'quiet' },
  empty: { label: 'No activity', tone: 'quiet' },
};

const withCommas = n => Number(n || 0).toLocaleString('en-US');

const formatConversion = (row) => {
  if (!row || !row.won) return '—';
  return `${(row.conversion * 100).toFixed(1)}%`;
};

const formatStock = (row) => {
  // "12 of 12" would read as sold out: an on-demand code is created already used.
  if (row && row.onDemand) return 'on demand';
  if (!row || !row.codesTotal) return 'no codes';
  return `${withCommas(row.codesUsed)} of ${withCommas(row.codesTotal)}`;
};

const seasonOf = (slugName) => {
  const match = /-(\d{4})(?:-|$)/.exec(slugName || '');
  return match ? match[1] : null;
};

const sortPrizes = (rows, field, direction) => {
  const dir = direction === 1 ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (typeof av === 'string' || typeof bv === 'string') {
      return dir * String(av || '').localeCompare(String(bv || '')) || a.slugName.localeCompare(b.slugName);
    }
    return dir * ((av || 0) - (bv || 0)) || a.slugName.localeCompare(b.slugName);
  });
};

const filterPrizes = (rows, { search = '', season = '', status = '' } = {}) => {
  const needle = (search || '').trim().toLowerCase();
  const s = season || '';
  const st = status || '';
  return rows.filter((row) => {
    if (st && row.status !== st) return false;
    if (s && seasonOf(row.slugName) !== s) return false;
    if (needle) {
      const haystack = `${row.slugName || ''} ${row.postName || ''}`.toLowerCase();
      if (haystack.indexOf(needle) === -1) return false;
    }
    return true;
  });
};

/*
CSV import (Task 9). Parses the raw text of a `code,link,scope` file — the shape
`importTicketCodes` expects, one row per redeemable slot.

Deliberately hand-rolled rather than a dependency (see task brief: no new deps).
Handles the one CSV wrinkle this data needs: a double-quoted cell that itself
contains a comma (link/scope are otherwise plain text, but this keeps the parser
honest rather than assuming it can never happen).

Repeated codes are NORMAL — many prizes are multi-use coupons where every row
shares one code. `duplicateCodeCount` is reported as an informational count and
the rows are never deduped or dropped.
*/
const REQUIRED_CSV_COLUMNS = ['code', 'link', 'scope'];

// Every real prize scope lives in this namespace (mirrors ps-back's own
// PRIZE = 'loyalty-prize' marker). A local prefix test, not an import from
// ps-back — this package has no dependency on it.
const LOYALTY_PRIZE_SCOPE_RE = /^loyalty-prize/;

// Split one CSV line into cells, honouring double-quoted cells that contain a
// comma (and doubled `""` as an escaped quote inside one).
const parseCsvLine = (line) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      // Toggles on ANY unquoted `"`, not only one at the start of a cell — so a value
      // like `foo"bar"baz` parses oddly (the interior quotes are swallowed rather than
      // kept literal). Safe for this feature's real inputs (alphanumeric codes, plain
      // URLs, hyphenated slug scopes never contain a stray `"`), but this would need a
      // proper RFC 4180 state machine (quote only recognised at cell start) before this
      // parser is reused for free-text CSV columns.
      inQuotes = true;
    } else if (char === ',') {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
};

const parseCodesCsv = (text) => {
  // Keep each surviving line's real 1-based position in the FILE (header is line 1),
  // so a lineError can point the operator at the exact row that is wrong.
  const lines = [];
  (text || '').split(/\r?\n/).forEach((raw, index) => {
    if (raw.trim() === '') return;
    lines.push({ raw, fileLine: index + 1 });
  });

  if (lines.length === 0) {
    return {
      headers: [],
      rows: [],
      missingColumns: REQUIRED_CSV_COLUMNS.slice(),
      extraColumns: [],
      scopes: [],
      duplicateCodeCount: 0,
      lineErrors: [],
    };
  }

  const headers = parseCsvLine(lines[0].raw).map(cell => cell.trim().toLowerCase());
  const missingColumns = REQUIRED_CSV_COLUMNS.filter(col => headers.indexOf(col) === -1);
  const extraColumns = headers.filter(col => REQUIRED_CSV_COLUMNS.indexOf(col) === -1);

  const codeIdx = headers.indexOf('code');
  const linkIdx = headers.indexOf('link');
  const scopeIdx = headers.indexOf('scope');

  const rows = [];
  const lineErrors = [];
  const scopeSet = new Set();
  const codes = [];

  for (let i = 1; i < lines.length; i++) {
    const { raw, fileLine } = lines[i];
    const cells = parseCsvLine(raw);

    if (cells.length !== headers.length) {
      // A malformed row cannot be read reliably even for the columns that ARE
      // required — record it and move on rather than guess which cell is which.
      lineErrors.push({
        line: fileLine,
        reason: `expected ${headers.length} column${headers.length === 1 ? '' : 's'}, found ${cells.length}`,
      });
    } else if (missingColumns.length === 0) {
      // A required column that is entirely missing from the header means there is
      // nothing meaningful to build a row from; the missing-column message already
      // blocks import, so parsing does not try to fabricate rows in that case.
      const code = cells[codeIdx].trim();
      const link = cells[linkIdx].trim();
      const scope = cells[scopeIdx].trim();
      rows.push({ code, link, scope });
      codes.push(code);
      if (scope) scopeSet.add(scope);
    }
  }

  const duplicateCodeCount = rows.length - new Set(codes).size;

  return {
    headers,
    rows,
    missingColumns,
    extraColumns,
    scopes: Array.from(scopeSet),
    duplicateCodeCount,
    lineErrors,
  };
};

// Feeds the leaderboard toolbar's Prize dropdown from the FULL catalogue
// (getLoyaltyPrizes — every prize, whether it has wins yet or not), not a
// paginated top-N by win count. That top-N source was the original defect:
// a prize with no wins could never appear in the dropdown, so selecting it
// from elsewhere (the Prizes drawer's "See the fans who won this") left the
// dropdown blank with no visible indication the filter was even active.
// `value` is always the slugName (what the filter matches on); `label`
// prefers postName only when it differs from slugName, since a handful of
// prizes have no post yet (hasPost: false, postName: null) or a post whose
// name is identical to its slug. Sorted by the visible label so the
// dropdown is alphabetically scannable — not by slugName, which is what the
// operator actually reads.
const buildPrizeSlugOptions = items => (items || [])
  .map(item => ({
    value: item.slugName,
    label: (item.postName && item.postName !== item.slugName) ? item.postName : item.slugName,
  }))
  .sort((a, b) => a.label.localeCompare(b.label) || a.value.localeCompare(b.value));

// Client-side page slice for the Prizes ledger (Task 10). `rows` is already
// filtered + sorted by the caller — this just takes the window for `page`
// (0-indexed). Out-of-range pages (negative size, page past the end) return
// an empty array rather than throwing, since the caller clamps separately.
const pageSlice = (rows, page, size) => {
  if (!Array.isArray(rows) || !size || size <= 0) return [];
  const from = page * size;
  if (from < 0 || from >= rows.length) return [];
  return rows.slice(from, from + size);
};

// Splits `rows` into slices no larger than `size` — the server caps a single
// `importTicketCodes` call at 5,000 rows and rejects (rather than truncates) a
// call over that cap, so the caller must chunk before sending.
const chunkRows = (rows, size) => {
  const chunkSize = size > 0 ? size : rows.length;
  const chunks = [];
  for (let i = 0; i < rows.length; i += chunkSize) {
    chunks.push(rows.slice(i, i + chunkSize));
  }
  return chunks;
};

// Every blocking condition LoyaltyImportModal enforces, each with its own message —
// the modal's whole contract is validate-before-send, so anything the server would
// reject must be caught here first. Repeated codes are DELIBERATELY absent from this
// list — 21 of 56 live prize pools are multi-use coupons where every row shares one
// code, and that must never block import. `prize` is the row the modal was opened
// pre-scoped from (drawer's "Add codes"), or falsy when opened unscoped (view header).
const buildImportBlockers = (parsed, prize) => {
  const blockers = [];

  if (parsed.missingColumns.length > 0) {
    blockers.push(`Missing required column${parsed.missingColumns.length > 1 ? 's' : ''}: ${parsed.missingColumns.join(', ')}.`);
  }

  if (parsed.scopes.length > 1) {
    blockers.push(`This file mixes ${parsed.scopes.length} scopes (${parsed.scopes.join(', ')}). One import can only carry one scope — split the file and import each separately.`);
  }

  if (prize && parsed.scopes.length === 1 && parsed.scopes[0] !== prize.slugName) {
    blockers.push(`This file's scope "${parsed.scopes[0]}" does not match ${prize.slugName}. Wrong file, or the wrong prize?`);
  }

  // Guards the UNSCOPED path (modal opened from the view header, no `prize` to compare
  // against): without this, a partner file whose scope column holds something outside
  // the loyalty-prize namespace (e.g. a dice.fm URL) would sail past every other client
  // blocker here and only get rejected server-side.
  if (parsed.scopes.length === 1 && !LOYALTY_PRIZE_SCOPE_RE.test(parsed.scopes[0])) {
    blockers.push(`This file's scope "${parsed.scopes[0]}" is outside the loyalty-prize namespace. Wrong file?`);
  }

  // Applies whether the modal was opened scoped or unscoped: a `scope` value on every
  // row is what tells the server which prize to credit, regardless of which prize (if
  // any) the modal happened to be opened from. Guarded on the `scope` HEADER being
  // present so this never fires alongside "missing required column" for the same root
  // cause (header entirely absent) — that case already has its own message above.
  if (parsed.missingColumns.indexOf('scope') === -1 && parsed.scopes.length === 0 && parsed.rows.length > 0) {
    blockers.push('The "scope" column is present but empty on every row — each row needs a scope value so its code lands against a prize.');
  }

  if (parsed.lineErrors.length > 0) {
    blockers.push(`${parsed.lineErrors.length} row${parsed.lineErrors.length > 1 ? 's' : ''} could not be read (first at line ${parsed.lineErrors[0].line}: ${parsed.lineErrors[0].reason}).`);
  }

  return blockers;
};

module.exports = {
  PRIZE_STATUS,
  formatConversion,
  formatStock,
  sortPrizes,
  filterPrizes,
  seasonOf,
  buildPrizeSlugOptions,
  pageSlice,
  parseCodesCsv,
  chunkRows,
  buildImportBlockers,
};
